import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import BlogEditor from '@/pages/blog/admin/components/BlogEditor';
import { initBlogStore, getPostById, createPost, updatePost, generateSlug, slugExists, type BlogArticle } from '@/hooks/useBlogStore';

export default function BlogEdit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = id === 'new' || !id;
  const postId = isNew ? null : parseInt(id, 10);

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [initialData, setInitialData] = useState<Partial<BlogArticle>>({});
  const [savedSlug, setSavedSlug] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      await initBlogStore();
      if (postId && !isNaN(postId)) {
        const post = await getPostById(postId);
        if (post) {
          setInitialData(post);
          setSavedSlug(post.slug);
        }
      }
      setLoading(false);
    };
    load();
  }, [postId]);

  const handleSave = useCallback(async (data: {
    title: string;
    excerpt: string;
    content: string;
    category: string;
    tags: string[];
    isPremium: boolean;
    coverImage: string;
  }) => {
    setSaving(true);
    try {
      const slug = generateSlug(data.title);
      const slugIsTaken = await slugExists(slug, postId ?? undefined);

      const finalSlug = slugIsTaken
        ? `${slug}-${Date.now().toString(36)}`
        : slug;

      const categoryLabels: Record<string, string> = {
        'daily-tips': 'Daily Tips',
        'mind-training': 'Mind Training',
        'routines': 'Routines',
        'nootropics': 'Nootropics',
        'productivity': 'Productivity',
        'wellness-science': 'Wellness Science',
      };

      const wordCount = data.content.replace(/<[^>]*>/g, '').split(/\s+/).filter(Boolean).length;
      const readTime = `${Math.max(1, Math.ceil(wordCount / 200))} min read`;

      const today = new Date().toISOString().split('T')[0];

      if (isNew) {
        const newPost = await createPost({
          slug: finalSlug,
          title: data.title,
          excerpt: data.excerpt,
          content: data.content,
          category: data.category,
          categoryLabel: categoryLabels[data.category] || data.category,
          author: 'ClearMind Editorial',
          date: today,
          readTime,
          image: data.coverImage || 'https://readdy.ai/api/search-image?query=Serene%20wellness%20blog%20cover%20with%20soft%20natural%20light%2C%20calm%20mindful%20atmosphere%2C%20clean%20minimalist%20aesthetic%2C%20warm%20peaceful%20tones%2C%20editorial%20wellness%20photography%2C%20soft%20focus%20background%2C%20harmonious%20composition&width=800&height=500&seq=blog-default-cover&orientation=landscape',
          imageAlt: data.title,
          tags: data.tags,
          isPremium: data.isPremium,
        });
        setSavedSlug(newPost.slug);
        navigate(`/blog/admin/${newPost.id}`, { replace: true });
      } else if (postId) {
        await updatePost(postId, {
          slug: finalSlug,
          title: data.title,
          excerpt: data.excerpt,
          content: data.content,
          category: data.category,
          categoryLabel: categoryLabels[data.category] || data.category,
          readTime,
          image: data.coverImage || initialData.image,
          imageAlt: data.title,
          tags: data.tags,
          isPremium: data.isPremium,
        });
        setSavedSlug(finalSlug);
      }
    } finally {
      setSaving(false);
    }
  }, [isNew, postId, navigate, initialData.image]);

  const previewUrl = savedSlug ? `/blog/${savedSlug}?ref=admin` : null;

  if (loading) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-24 pb-20 flex items-center justify-center">
          <i className="ri-loader-4-line animate-spin text-2xl text-foreground-400" />
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main className="pt-24 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-8">
            <div>
              <button
                type="button"
                onClick={() => navigate('/blog/admin')}
                className="inline-flex items-center gap-1 text-sm text-foreground-600 hover:text-foreground-950 font-label transition-colors mb-1"
              >
                <i className="ri-arrow-left-line" />
                Back to Admin
              </button>
              <h1 className="font-heading text-2xl font-bold text-foreground-950">
                {isNew ? 'New Post' : 'Edit Post'}
              </h1>
            </div>
            {previewUrl ? (
              <Link
                to={previewUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap font-label font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 border border-primary-300 bg-transparent text-primary-600 hover:bg-primary-50 h-10 rounded-lg text-sm px-5"
                title="Preview live post"
              >
                <i className="ri-eye-line" />
                Preview
              </Link>
            ) : (
              <button
                type="button"
                disabled
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap font-label font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 disabled:pointer-events-none disabled:opacity-50 border border-primary-300 bg-transparent text-primary-600 h-10 rounded-lg text-sm px-5"
                title="Save first to enable preview"
              >
                <i className="ri-eye-line" />
                Preview
              </button>
            )}
          </div>

          <div className="bg-background-50 border border-background-200/70 rounded-2xl p-6 sm:p-8">
            <BlogEditor
              initialTitle={initialData.title || ''}
              initialExcerpt={initialData.excerpt || ''}
              initialContent={initialData.content || ''}
              initialCategory={initialData.category || 'daily-tips'}
              initialTags={initialData.tags || []}
              initialIsPremium={initialData.isPremium || false}
              initialCoverImage={initialData.image || ''}
              onSave={handleSave}
              previewUrl={previewUrl}
              isSaving={saving}
            />
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}